import {createContext, ReactNode, useContext, useRef, useState} from "react";
import useAudioRecording from "../hooks/useAudioRecording.tsx";
import {AlertsContext} from "./AlertsContext.tsx";
import {useTranslation} from "react-i18next";
import {Optional} from "../utils/types.ts";
import {Patient} from "./PatientsContext.tsx";
import DiscardRecordingConfirmationModal from "../components/Sessions/DiscardRecordingConfirmationModal.tsx";
import {createNewRecording} from "../api/recordings.ts";
import {RecordingsContext, RecordingUnparsed} from "./RecordingsContext.tsx";

type AudioRecordingContextType = {
  isRecording: boolean;
  isPaused: boolean;
  startRecording: () => void;
  stopRecording: () => void;
  pauseRecording: () => void;
  resumeRecording: () => void;
  getRecordingDuration: () => string;
  durationInSeconds: number;
  recordingDate: Optional<Date>;
  setRecordingDate: (date: Optional<Date>) => void;
  setConfirmationModalOpen: (open: boolean) => void;
  toggleSize: () => void;
  shrunk: boolean;
  handleAssignPatient: (patient: Patient) => void;
  patient: Optional<Patient>;
}

const defaultValues: AudioRecordingContextType = {
  isRecording: false,
  isPaused: false,
  startRecording: () => {},
  stopRecording: () => {},
  pauseRecording: () => {},
  resumeRecording: () => {},
  getRecordingDuration: () => '00:00:00',
  durationInSeconds: 0,
  recordingDate: null,
  setRecordingDate: () => {},
  setConfirmationModalOpen: () => {},
  toggleSize: () => {},
  shrunk: false,
  handleAssignPatient: () => {},
  patient: null,
}

const AudioRecordingContext = createContext<AudioRecordingContextType>(defaultValues);

function AudioRecordingProvider({children}: {children: ReactNode}) {
  
  const { prependNewSession, replaceSession } = useContext(RecordingsContext);
  const { isRecording, setIsRecording, isPaused, setIsPaused, audioChunks, mediaRecorder, mediaStream, recordingDate, setRecordingDate } = useAudioRecording();
  const { showAlert } = useContext(AlertsContext);
  const {t} = useTranslation();
  const [timeCounter, setTimeCounter] = useState(0);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [shrunk, setShrunk] = useState(false);
  const [patient, setPatient] = useState<Optional<Patient>>(null);
  const patientRef = useRef<Optional<Patient>>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const discardRef = useRef<boolean>(false);
  
  function startRecording() {
    if(isRecording) return;
    navigator.mediaDevices.getUserMedia({ audio: true })
      .then(stream => {
        // start counter and set it to 0, and update on every second
        if(intervalRef.current) clearInterval(intervalRef.current);
        discardRef.current = false;
        setPatient(null);
        patientRef.current = null;
        setTimeCounter(0);
        intervalRef.current = setInterval(() => {
          setTimeCounter(prev => prev + 1);
        }, 1000);

        audioChunks.current = [];

        mediaStream.current = stream;
        mediaRecorder.current = new MediaRecorder(stream);

        mediaRecorder.current.ondataavailable = event => {
          audioChunks.current.push(event.data);
        }

        mediaRecorder.current.onstop = async () => {
          if(discardRef.current) return;
          const audioBlob = new Blob(audioChunks.current, { type: 'audio/wav' });
          const fileName = `recording-${Date.now()}.wav`;
          const file = [new File([audioBlob], fileName, { type: 'audio/wav' })];
          const date = prependNewSession(timeCounter);
          const response: RecordingUnparsed = await createNewRecording(file, patientRef.current);
          replaceSession(date, response);
        }

        mediaRecorder.current.start();
        setIsRecording(true);
        setRecordingDate(new Date());
      })
      .catch(() => {
        showAlert({message: t('sessions.errorAccessingMicrophone'), type: 'error'});
      });
  }
  
  function stopRecording() {
    if(!mediaRecorder.current) return;
    if(mediaRecorder.current.state !== 'inactive') mediaRecorder.current.stop();
    if(mediaStream.current) mediaStream.current.getTracks().forEach(track => track.stop());
    if(intervalRef.current) clearInterval(intervalRef.current);
    setIsRecording(false);
    setRecordingDate(null);
    if(intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }
  
  function pauseRecording() {
    if(!mediaRecorder.current) return;
    if(mediaRecorder.current.state !== 'paused') mediaRecorder.current.pause();
    if(intervalRef.current) clearInterval(intervalRef.current);
    setIsPaused(true);
  }
  
  function resumeRecording() {
    if(!mediaRecorder.current) return;
    if(mediaRecorder.current.state !== 'recording') mediaRecorder.current.resume();
    if(intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        setTimeCounter(prev => prev + 1);
      }, 1000);
    }
    setIsPaused(false);
  }
  
  // Returns a string to display in hh:mm:ss format
  function getRecordingDuration() {
    const seconds = timeCounter % 60;
    const minutes = Math.floor(timeCounter / 60) % 60;
    const hours = Math.floor(timeCounter / 3600);
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }
  
  function discardRecording() {
    discardRef.current = true;
    if(mediaRecorder.current) {
      mediaRecorder.current.stream.getTracks().forEach(track => track.stop());
      mediaRecorder.current = null;
    }
    setIsRecording(false);
    setRecordingDate(null);
    if(intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setTimeCounter(0);
    showAlert({message: t('sessions.recordingDiscarded'), type: 'success'});
  }
  
  function toggleSize() {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    document.startViewTransition ?
      document.startViewTransition(() => {
        setShrunk(prev => !prev);
      }) :
      setShrunk(prev => !prev);
  }
  
  function handleAssignPatient(patientToAssign: Optional<Patient>) {
    patientRef.current = patientToAssign;
    setPatient(patientToAssign);
  }
  
  return (
    <AudioRecordingContext.Provider value={{
      isRecording,
      isPaused,
      startRecording,
      stopRecording,
      pauseRecording,
      resumeRecording,
      getRecordingDuration,
      durationInSeconds: timeCounter,
      recordingDate,
      setRecordingDate,
      setConfirmationModalOpen,
      toggleSize,
      shrunk,
      handleAssignPatient,
      patient,
    }}>
      {children}
      <DiscardRecordingConfirmationModal open={confirmationModalOpen} setOpen={setConfirmationModalOpen} handleDiscard={discardRecording}/>
    </AudioRecordingContext.Provider>
  )
}

export {AudioRecordingContext, AudioRecordingProvider};